import { appEventModule } from "app/event"
import { APP_CONTEXTS, APP_EVENTS, IRootState } from "app/model"
import { appActionCreator } from "app/store/actions"
import { getStore } from "app/store/store"
import { mergeArrays } from "d3-tools/tools/common"

export function getActiveAppContext(state?: IRootState): APP_CONTEXTS | null {
  return (state || getStore().getState()).app.activeContext
}

export function setActiveAppContext(context: APP_CONTEXTS | null): void {
  getStore().dispatch(appActionCreator.setAppContext(context))
  appEventModule.notify(APP_EVENTS.APP_CONTEXTS_CHANGE, context as string)
}

export function getRouteBasePath(): string {
  return window.APP_OPTIONS?.urlBasePath || ""
}

export function parseBoolean(value: string | number | undefined): boolean | undefined {
  if (typeof value === "number" || typeof value === "undefined") {
    value = String(value)
  }
  switch (value.toLowerCase()) {
    case "true":
    case "1":
      return true
    case "false":
    case "0":
      return false
    default:
      return undefined
  }
}

export function isDate(date: any): boolean {
  return Object.prototype.toString.call(date) === "[object Date]"
}

export function getEmptyArrayIfNotDefined<T>(array: T[]): T[] {
  if (array === null || array === undefined) {
    return []
  }
  return array
}

export function isDefined(value: any): boolean {
  return value !== null && value !== undefined
}

export const objectHasValue = (object: any, value: any): boolean => {
  if (typeof object !== "object") {
    throw Error("Bad 'object' parameter : not an object !")
  }
  for (const key in object) {
    if (object.hasOwnProperty(key)) {
      if (object[key] === value) {
        return true
      }
    }
  }
  return false
}

export function capitalize(str: string): string {
  if (!str) {
    return str
  }
  return str.slice(0, 1).toUpperCase() + str.slice(1)
}

export function isStringNumber(id: string | number): boolean {
  return Number.isInteger(Number(id))
}

export function isEmptyObject(object: any): boolean {
  return typeof object === "object" && JSON.stringify(object) === "{}"
}

// @recursive
export function deepEquals(element1: any, element2: any): boolean {
  if (typeof element1 !== typeof element2) {
    return false
  }
  if (typeof element1 !== "object" || element1 === null || element1 === undefined) {
    return element1 === element2
  }
  if (element2 === null || element2 === undefined) {
    return false
  }
  if (element1 instanceof Date) {
    return element1.getTime() === element2.getTime()
  }
  const element1Keys = Object.keys(element1)
  const element2Keys = Object.keys(element2)
  if (element1Keys.length !== element2Keys.length) {
    return false
  }
  for (const key of element1Keys) {
    // @recursivity
    if (!deepEquals(element1[key], element2[key])) {
      return false
    }
  }
  return true
}

// @recursive
export function deepCopy<T>(element: T): T {
  if (typeof element !== "object" || element === null) {
    // not an object return primitive value
    return element
  }
  if (element instanceof Date) {
    return new Date((element as Date).getTime()) as any
  }
  const copy: any = Array.isArray(element) ? [] : {}
  for (const key of Object.keys(element)) {
    // @recursivity
    copy[key] = deepCopy((element as any)[key])
  }
  return copy
}

// @recursive
export function deepMerge(element1: any, element2: any): any {
  if (!element1) {
    if (element2 !== undefined && element2 !== null) {
      return element2
    }
    return element1
  }
  if (!element2) {
    return element1
  }
  if (typeof element1 !== "object" || typeof element2 !== "object") {
    return deepCopy(element2)
  }
  const deepElement1Copy = deepCopy(element1)
  if (Array.isArray(element1)) {
    if (Array.isArray(element2)) {
      return mergeArrays(element1, element2)
    }
    return element2
  } else if (Array.isArray(element2)) {
    return element2.slice()
  }
  if (typeof element1 !== "object" || typeof element2 !== "object") {
    throw Error("Should be objects here")
  }
  const element2Keys = Object.keys(element2)
  for (const element2Key of element2Keys) {
    // @recursivity
    deepElement1Copy[element2Key] = deepMerge(deepElement1Copy[element2Key], element2[element2Key])
  }
  return deepElement1Copy
}

export function notifyWindowResize(): void {
  appEventModule.notify(APP_EVENTS.WINDOW_RESIZE)
}

export function getPercent(currentValue: number, totalValue: number): number {
  if (totalValue === 0 || currentValue === 0) {
    return 0
  }
  return Number(((100 * currentValue) / totalValue).toFixed(2))
}

export function getDifference(initialValue: number | undefined, newValue: number | undefined): number {
  if (newValue !== undefined && initialValue !== undefined) {
    return newValue - initialValue
  }
  if (newValue === undefined && typeof initialValue === "number") {
    return -initialValue
  }
  if (initialValue === undefined && typeof newValue === "number") {
    return newValue
  }
  return 0
}

export function fillZero(value: number): string {
  if (value < 10) {
    return `0${value}`
  }
  return String(value)
}

export function getRandomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min)) + min
}

// ex:
//   - obj => var test = { main: { orderId: "pouet"} }
//   - key => "main.orderId"
// Will return "pouet"
export function getObjectProperty(obj: any, key: string): any {
  return key.split(".").reduce((subObj, subKey) => subObj[subKey], obj)
}

// will set sub properties, ex key can be "main.orderId"
export function setValueAndGetObject(obj: any, key: string, value: any): any {
  const props = key.split(".")
  let currentObject = obj
  for (let i = 0; i < props.length; i++) {
    const prop = props[i]
    if (i === props.length - 1) {
      currentObject[prop] = value
      return obj
    }
    currentObject = currentObject[prop]
  }
  return obj
}

export function getStringSameStartChars(string1: string, string2: string): string {
  const smallerString = string1.length < string2.length ? string1 : string2
  const longerString = string1.length < string2.length ? string2 : string1
  let sameStartWith: string = ""
  for (let i = 0; i < smallerString.length; i++) {
    const currentChar = smallerString[i]
    if (currentChar === longerString[i]) {
      sameStartWith += currentChar
    } else {
      break
    }
  }
  return sameStartWith
}

export const arrayMove = <T>(array: T[], fromIndex: number, toIndex: number): T[] => {
  array = array.slice()
  const element = array[fromIndex]
  array.splice(fromIndex, 1)
  array.splice(toIndex, 0, element)
  return array
}

export const wait = async (timeInMs: number) => await new Promise(resolve => setTimeout(resolve, timeInMs))
